import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { thinkingPluginKey } from '../pm-plugins/thinking-plugin';
import {
	addEntryPointClickedAnalytics,
	addRetryThinkingAnalytics,
	fireAiInteractionInitiatedUnifiedAnalytics,
} from '../ui/utils/analytics';
import type { SetThinkingStateAction } from './types';

/**
 * Single editor-action for every thinking transition: sets the plugin status.
 * When moving to `thinking` it also fires the relevant analytics:
 * - `entryPoint` → entry-point + unified aiInteraction analytics (start).
 * - `isRetry`    → the error-screen retry-button analytics (retry).
 * When moving to `error`, `template` selects the error-screen copy/links.
 */
export const setThinkingStateAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined): SetThinkingStateAction =>
	(state, options): boolean =>
		api?.core?.actions.execute(({ tr }) => {
			if (state === 'thinking') {
				if (options?.entryPoint) {
					// TODO: EDITOR-8269 - placeholder id; may come from the backend once the
					// unified suggested-edits analytics structure is finalised.
					const singleInstrumentationId = crypto.randomUUID();
					addEntryPointClickedAnalytics(api?.analytics?.actions)(tr, options.entryPoint);
					fireAiInteractionInitiatedUnifiedAnalytics(
						api?.analytics?.actions,
						options.entryPoint,
						singleInstrumentationId,
					);
				}
				if (options?.isRetry) {
					addRetryThinkingAnalytics(api?.analytics?.actions)(tr);
				}
			}
			tr.setMeta(thinkingPluginKey, {
				status: state,
				...(state === 'error' && options?.template
					? { error: { template: options.template } }
					: {}),
			});
			return tr;
		}) || false;
